import { Box, Chip, CircularProgress, Fade, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { AttachmentTypeEnum } from "src/Shared/Enums/AttachmentType.enum";
import { useModal, useStore } from "src/Shared/Hooks";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { ToastService } from "src/Shared/Services";
import CompanyService from "../../Services/Company.service";
import { ClientStatus } from "../../Enums/ClientStatus.enum";
import { CLIENT_TYPE_OPTIONS } from "../../Enums/ClientType.enum";
import { VendorType } from "../../Enums/VendorType";
import { IRouteParams } from "../../Interfaces";
import { IUpdateClientDetails } from "../../Interfaces/IUpdateClientDetails";
import IndustryInfoDialog from "../AddOrEditCompany/IndustryInfoDialog";
import { fields } from "./fields";
import { COUNTRY_OPTIONS } from "../../Enums/Country.enum";
import { areDealFiguresSelfReported } from "./ClientDetails.rules";

const AUTOSAVE_DELAY_MS = 800;
// Matches the transient-feedback dwell the grids already use for a row flash, so the two kinds of
// "that worked" on this record stay on the same clock.
const SAVED_VISIBLE_MS = 2500;

/**
 * What the page is showing about the last autosave attempt.
 *
 * "saved" is transient and clears itself; "blocked" persists, because the edit is still unsaved and
 * saying so once and then hiding it would put the user back where bug 5839 left them.
 */
type SaveNotice =
  | { kind: "none" }
  /** The request is in flight. Without this the only signal was a flash after it had finished. */
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "blocked"; message: string };


// Re-exported so existing importers of this page keep working.
export { COUNTRY_OPTIONS };

/**
 * The client record's Details page (CR.4 as amended by decision 18 — see bug #5640).
 *
 * It is a SUPERSET, not a mirror of wizard step 1: the full existing CRM field set
 * (SRS §6.3.1) plus the step-1 onboarding fields alongside it, so onboarding capture
 * and CRM record maintenance stay separate processes over one shared record and
 * nothing captured against existing customers is lost. Shared fields are merged —
 * a value edited here or in the wizard is the same stored value.
 *
 * Saved as you type with no Save button, and no required-field rule blocks an edit:
 * completion gating belongs to the wizard. Editable before, during and after
 * onboarding — the frozen artefact is the submitted onboarding record, not this
 * page (decision 6).
 */
const ClientDetails: React.FC = () => {
  const { companyRowID } = useParams<Partial<IRouteParams>>();
  const companyForm = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => companyForm.$(key);
  const modal = useModal();

  const {
    rootStore: {
      companyStore: {
        company,
        companyLoading,
        getCompany,
        getCompanyOverview,
        getProspectsCount,
        resetCompanyList,
      },
      commonStore: {
        dealStatusList,
        supportiveRegions,
        vendorTypes,
        industries,
        leadTypeList,
        serviceTypeList,
        clientStatus,
        franchiseOffices,
        loadCommonData,
      },
    },
  } = useStore();

  const [ready, setReady] = useState<boolean>(false);
  /**
   * What the page is currently telling the user about the last autosave.
   *
   * A boolean only covered the happy path, so every reason an autosave did NOT happen was silent:
   * no save, no confirmation, and not even the inline field error the code assumed was showing —
   * mobx-react-form renders none until showErrors() is called. An edit could be discarded with the
   * page looking exactly as it does when it succeeds (bug 5839).
   */
  const [notice, setNotice] = useState<SaveNotice>({ kind: "none" });
  const lastSavedRef = useRef<string>("");
  const savedTimerRef = useRef<any>(null);

  // The Saved chip clears itself on a timer, which must not outlive the page.
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  /**
   * An archived client is readable and not writable (CR.11).
   *
   * Deleting a client that has onboarding data archives the row instead of destroying it, and the
   * record's own endpoints never re-checked the flag — so opening an archived client's URL gave a
   * fully editable Details page that saved as you typed. This page is the worst case of that, because
   * it has no Save button to leave disabled: every keystroke was a write.
   */
  const isArchived = Boolean(company?.isArchived);

  useEffect(() => {
    loadCommonData();
    // Autosave stays off until this record's own values are in the form — otherwise
    // switching clients could write the previous client's values onto this one.
    setReady(false);
    lastSavedRef.current = "";
    // No app-level loader: the header and sidebar stay put while this section loads.
    if (companyRowID) getCompany(companyRowID, false);
  }, [companyRowID]);

  useEffect(() => {
    if (!company || company.companyRowID !== companyRowID) return;
    companyForm.set(company);
    // Special Pricing is a Yes/No radio — an unset flag reads as No, as on the
    // production form.
    if (getField("specialPricing").value == null)
      getField("specialPricing").set("value", false);
    lastSavedRef.current = JSON.stringify(buildRequest());
    setReady(true);
  }, [company]);

  // Attachments follow their vendor type, exactly as on the production form.
  useEffect(() => {
    if (!ready) return;
    const selected = getField("vendorTypeIDs").value;
    if (!selected?.some((x) => x == VendorType.TDDA))
      getField("tddaAttachments").set("value", []);
    if (!selected?.some((x) => x == VendorType.Healthtick))
      getField("healthTickAttachments").set("value", []);
  }, [getField("vendorTypeIDs").value]);

  const isHealthTickClient = () =>
    Boolean(getField("vendorTypeIDs").value?.some((x) => x == VendorType.Healthtick));

  // Whether this client's pipeline figures are its own or derived from its deals (bug 5716). Taken
  // from the form so changing Status here flips the two controls straight away.
  const selfReportedFigures = areDealFiguresSelfReported(
    getField("clientStatusID").value ?? company?.clientStatusID,
  );

  const isTddaClient = () =>
    Boolean(getField("vendorTypeIDs").value?.some((x) => x == VendorType.TDDA));

  const healthTickManagerVisiblity = () => {
    const isVisible = isHealthTickClient();
    if (!isVisible) getField("healthTickPrimaryRelationshipManager").set("value", null);
    return isVisible;
  };

  const onFranchiseChange = (value: number) => {
    if (!value) return;
    const primaryOffice = franchiseOffices.find(
      (x) => x.franchiseID == value && x.isPrimary === true,
    );
    getField("franchiseOfficeID").set("value", primaryOffice ? primaryOffice.value : null);
  };

  const toNumberOrNull = (value: any): number | null => {
    if (value === null || value === undefined || `${value}`.trim() === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const buildRequest = (): IUpdateClientDetails => {
    const values = companyForm.values();
    return {
      companyRowID: companyRowID,
      companyName: values.companyName || "",
      clientStatusID: values.clientStatusID ?? null,
      companyCode: values.companyCode || null,
      countryCode: values.countryCode || null,
      subDivisionCode: values.subDivisionCode || null,
      franchiseID: values.franchiseID ?? null,
      franchiseOfficeID: values.franchiseOfficeID ?? null,
      companyType: values.companyType || null,
      // One value since D12 (story 6348); "" clears it server-side, null would leave it alone.
      clientType:
        typeof values.clientType === "string" ? values.clientType : values.clientType?.value ?? "",
      headOfficePhone: values.headOfficePhone || null,
      leadTypeID: values.leadTypeID ?? null,
      industryID: values.industryID ?? null,
      website: values.website || null,
      totalStaffNumber: toNumberOrNull(values.totalStaffNumber),
      specialPricing: Boolean(values.specialPricing),
      // Sent always; the server ignores both unless the client is still onboarding (bug 5716).
      salesPipelineValue: toNumberOrNull(values.salesTotalValue),
      dealStatusID: values.dealStatusID ?? null,
      vendorTypeIDs: values.vendorTypeIDs || [],
      serviceTypeIDs: values.serviceTypeIDs || [],
      primaryCompanyRelationshipManager:
        values.primaryCompanyRelationshipManager || null,
      healthTickPrimaryRelationshipManager:
        values.healthTickPrimaryRelationshipManager || null,
      additionalCompanyRelationshipManagers:
        values.additionalCompanyRelationshipManagers || [],
      attachments: [
        ...(values.tddaAttachments || []),
        ...(values.healthTickAttachments || []),
      ],
    };
  };

  // Reading form values in render makes this observer re-render on every edit;
  // the changed payload then autosaves after a short pause — no Save button.
  const valuesJson = ready ? JSON.stringify(buildRequest()) : "";

  useEffect(() => {
    // Nothing on an archived client is scheduled, let alone sent.
    if (isArchived) return;
    if (!ready || !valuesJson || valuesJson === lastSavedRef.current) return;
    const timer = setTimeout(() => save(valuesJson), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [valuesJson, ready, isArchived]);

  const save = (json: string) => {
    const request: IUpdateClientDetails = JSON.parse(json);
    if (request.companyRowID !== companyRowID) return;

    // The two reasons an edit is not persisted are now said out loud. Neither blocks the page — the
    // user keeps typing and the next valid pause saves — but leaving the page looking identical to a
    // successful save is what bug 5839 reported.
    //
    // An empty name still never blocks: it just isn't persisted until typed. There is no required
    // rule behind it, so showErrors() would highlight nothing and the notice has to name it.
    if (!request.companyName || !request.companyName.trim()) {
      setNotice({ kind: "blocked", message: "Not saved — a client needs a name" });
      return;
    }
    // No field is required here, so this only holds back a malformed value (a bad URL, a non-numeric
    // head count). Its inline error is not on screen until it is asked for, which is why an invalid
    // edit used to vanish in silence.
    if (!companyForm.isValid) {
      companyForm.showErrors();
      setNotice({
        kind: "blocked",
        message: "Not saved — check the highlighted fields",
      });
      return;
    }
    // Said before the request, not after it, so the page acknowledges the edit at the moment it acts
    // on it rather than only in the instant it finishes.
    clearTimeout(savedTimerRef.current);
    setNotice({ kind: "saving" });

    CompanyService.updateClientDetails(request).subscribe({
      next: () => {
        lastSavedRef.current = json;
        setNotice({ kind: "saved" });
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(
          () => setNotice({ kind: "none" }),
          SAVED_VISIBLE_MS,
        );
        // A status change moves the client between Home and Prospects immediately.
        // Silent: an edit must not blank the record behind a full-page loader.
        getCompanyOverview(companyRowID, true);
        getProspectsCount();
        resetCompanyList();
      },
      error: (error: AxiosError) => {
        // The toast says what went wrong; the chip says the edit is still unsaved, which outlives it.
        setNotice({
          kind: "blocked",
          message: "Not saved — the change could not be sent",
        });
        ToastService.axiosError(error);
      },
    });
  };

  // Once an onboarding has been submitted there is no route back to Prospect
  // (Feature 1 guard).
  const statusOptions =
    company?.hasSubmittedOnboarding &&
    company?.clientStatusID !== ClientStatus.Prospect
      ? clientStatus.filter((x) => x.value !== ClientStatus.Prospect)
      : clientStatus;

  const gridProps: IGridFormControlProps = {
    row: 2,
    col: 15,
    spacing: 4,
    itemResponsive: {
      lg: 6,
      md: 6,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("companyName"),
        fieldKey: "companyName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("clientStatusID"),
        fieldKey: "clientStatusID",
        type: EDITOR_TYPES.DROPDOWN,
        options: statusOptions,
      },
      {
        // Not marked required here (a wizard-only requirement), but the CRM and
        // Imperans uniqueness messages still surface on a clash (CR.4).
        field: getField("companyCode"),
        fieldKey: "companyCode",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("countryCode"),
        fieldKey: "countryCode",
        type: EDITOR_TYPES.DROPDOWN,
        options: COUNTRY_OPTIONS,
      },
      {
        field: getField("subDivisionCode"),
        fieldKey: "subDivisionCode",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("franchiseID"),
        fieldKey: "franchiseID",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options: supportiveRegions,
        onValueChange(franchise, _) {
          onFranchiseChange(franchise?.value);
        },
      },
      {
        field: getField("franchiseOfficeID"),
        fieldKey: "franchiseOfficeID",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options:
          franchiseOffices.filter(
            (x) => x.franchiseID == getField("franchiseID").value,
          ) || [],
      },
      {
        // The same Client Type step 1 offers — National, Regional or SSO since D12 (story 6348) —
        // so SSO can be captured or corrected from the record (bug 5705). Mirrored to the CRM
        // National/Regional column server-side, so this stays one stored value (S1.8).
        field: getField("clientType"),
        fieldKey: "clientType",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options: CLIENT_TYPE_OPTIONS,
      },
      {
        // Step 1 captures this too, but the wizard freezes on submission and the record does not
        // (decision 6) — without it here a submitted client could never correct its phone number.
        field: getField("headOfficePhone"),
        fieldKey: "headOfficePhone",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("vendorTypeIDs"),
        fieldKey: "vendorTypeIDs",
        type: EDITOR_TYPES.DROPDOWN,
        multiple: true,
        options: vendorTypes,
      },
      {
        field: getField("specialPricing"),
        fieldKey: "specialPricing",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          { label: "Yes", value: true },
          { label: "No", value: false },
        ],
      },
      {
        field: getField("industryID"),
        fieldKey: "industryID",
        type: EDITOR_TYPES.DROPDOWN,
        options: industries,
        infoIcon: true,
        onInfoIconClick() {
          modal.open(
            <IndustryInfoDialog
              industries={industries}
              selectedIndustryId={getField("industryID").value}
            />,
          );
        },
      },
      {
        field: getField("website"),
        fieldKey: "website",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("totalStaffNumber"),
        fieldKey: "totalStaffNumber",
        type: EDITOR_TYPES.TEXT_FIELD,
        // A head count has no negative. Declaring the floor is what makes the input refuse the
        // minus key and a pasted negative; zero stays typeable because this field has no minimum.
        inputProps: { min: 0 },
      },
      {
        field: getField("leadTypeID"),
        fieldKey: "leadTypeID",
        type: EDITOR_TYPES.DROPDOWN,
        options: leadTypeList.filter(
          (x) => x.isActive || x.value === getField("leadTypeID").value,
        ),
      },
      {
        field: getField("serviceTypeIDs"),
        fieldKey: "serviceTypeIDs",
        type: EDITOR_TYPES.DROPDOWN,
        multiple: true,
        options: serviceTypeList,
      },
      {
        field: getField("primaryCompanyRelationshipManager"),
        fieldKey: "primaryCompanyRelationshipManager",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
      },
      {
        field: getField("healthTickPrimaryRelationshipManager"),
        fieldKey: "healthTickPrimaryRelationshipManager",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        hidden: !healthTickManagerVisiblity(),
      },
      {
        field: getField("additionalCompanyRelationshipManagers"),
        fieldKey: "additionalCompanyRelationshipManagers",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
        multiple: true,
      },
      {
        field: getField("tddaAttachments"),
        fieldKey: "tddaAttachments",
        multiple: true,
        type: EDITOR_TYPES.FILE_UPLOADER,
        attachmentType: AttachmentTypeEnum.TDDA,
        hidden: !isTddaClient(),
      },
      {
        field: getField("healthTickAttachments"),
        fieldKey: "healthTickAttachments",
        multiple: true,
        type: EDITOR_TYPES.FILE_UPLOADER,
        attachmentType: AttachmentTypeEnum.HealthTick,
        hidden: !isHealthTickClient(),
      },
      // Sales Pipeline Value and Deal Stage change hands when the client is promoted (bug 5716).
      //
      // While it is a prospect or onboarding it has no sales deals, so there is nothing to derive
      // these from and the relationship manager enters them here. Once it is a current client the
      // real deals exist and are authoritative, so both go back to being derived and read-only —
      // editing them then would mean two different numbers both claiming to be the pipeline value.
      //
      // The server decides which state applies and sends areDealFiguresSelfReported, so the screen
      // and the API cannot disagree about whether a save will be accepted.
      {
        field: getField("salesTotalValue"),
        fieldKey: "salesTotalValue",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: selfReportedFigures,
        isMasked: true,
        helperText: selfReportedFigures
          ? "The expected value of this deal. Replaced by the sum of the client's sales deals once they become a current client."
          : "Sum of this client's open sales deals — maintained in Sale Deals.",
      },
      {
        // The picklist while it is the client's own answer…
        field: getField("dealStatusID"),
        fieldKey: "dealStatusID",
        type: EDITOR_TYPES.DROPDOWN,
        options: dealStatusList,
        hidden: !selfReportedFigures,
        helperText: "The stage this deal is at. Read from the client's sales deals once they become a current client.",
      },
      {
        // …and the derived summary once it is not. One of the two always renders, never both.
        field: getField("dealStage"),
        fieldKey: "dealStage",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: selfReportedFigures,
        helperText: "Taken from this client's sales deals — set the stage on the deal itself.",
      },
      {
        field: getField("createdByOn"),
        fieldKey: "createdByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
      },
      {
        field: getField("modifiedByOn"),
        fieldKey: "modifiedByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
      },
      {
        field: getField("importedByOn"),
        fieldKey: "importedByOn",
        type: EDITOR_TYPES.TEXT_FIELD,
        isEditingMode: false,
        hidden: !Boolean(getField("importedByOn").value),
      },
    ],
    form: companyForm,
  };

  /**
   * On an archived client every control renders as read-only text.
   *
   * Done here rather than on each of the thirty-odd controls above so a field added later cannot
   * forget: the whole set is turned read-only in one place, and the individual isEditingMode
   * decisions already made (self-reported deal figures, the import audit fields) still hold because
   * false is the value being forced.
   */
  const shownGridProps: IGridFormControlProps = isArchived
    ? {
        ...gridProps,
        inputControls: gridProps.inputControls.map((control) => ({
          ...control,
          isEditingMode: false,
        })),
      }
    : gridProps;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1,
          // A confirmation nobody can see is not a confirmation. This is a 27-field form on a page
          // that scrolls as a whole, so a chip beside the title spent its two seconds above the top
          // of the viewport for any edit below the first screenful — which is why the record looked
          // like it saved silently (bug 5839). Sticking the row keeps the notice where the person
          // typing is actually looking, and keeps it beside the page title as the story asks.
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "#f7f7f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5">Details</Typography>
          {companyLoading && !ready && <CircularProgress size={18} />}
          {/* Said plainly, because this page has no Save button to grey out: without it, fields that
              simply refuse to change look like a broken form rather than an archived record. */}
          {isArchived && (
            <Chip label="Archived — read-only" color="default" size="small" />
          )}
          {notice.kind === "saving" && (
            <Chip
              label="Saving…"
              size="small"
              variant="outlined"
              icon={<CircularProgress size={12} thickness={6} />}
            />
          )}
          <Fade in={notice.kind === "saved"}>
            <Chip label="Saved" color="success" size="small" />
          </Fade>
          {notice.kind === "blocked" && (
            <Chip
              label={notice.message}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        {company?.isCompanyCodeAutoGenerated && (
          <Typography variant="caption" color="text.secondary">
            Company code was auto-generated — editing it makes it yours.
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        <GridFormControl {...shownGridProps} />
      </Box>
    </>
  );
};

export default observer(ClientDetails);
